import { AddRounded, DeleteRounded, DriveFolderUploadRounded, EditRounded } from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	Divider,
	IconButton,
	LinearProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemButton,
	ListItemText,
	MenuItem,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import AddViewImagesModal from "../AddViewImagesModal/AddViewImagesModal";
import { GlobalState } from "context/GlobalContext";
import { shootSkuImagesAssoc } from "../../CreateShoot/data";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { uploadImageFileTypes, uploadImageMimeTypes } from "data/globalData";

const fallbackChannel = "SHOPIFY";
const availableChannels = [
	{ label: "SHOPIFY", value: "SHOPIFY" },
	{ label: "AJIO", value: "AJIO" },
	{ label: "MYNTRA", value: "MYNTRA" },
];
const allowedChannelValues = new Set(availableChannels.map((channel) => channel.value));

const normalizeKey = (value = "") =>
	value
		.trim()
		.replace(/\s+/g, "_")
		.replace(/[^A-Za-z0-9_]/g, "")
		.toUpperCase();

const formatLabel = (value = "") => value.split("_").join(" ").toLowerCase();

const getImageChannel = (image) => image?.channel || fallbackChannel;
const acceptedImageExtensions = new Set(uploadImageFileTypes.map((type) => `.${type}`));

const isAllowedImageFile = (file) => {
	const fileName = file?.name?.toLowerCase?.() ?? "";
	const extension = fileName.includes(".") ? fileName.slice(fileName.lastIndexOf(".")) : "";
	return uploadImageMimeTypes.includes(file?.type) || acceptedImageExtensions.has(extension);
};

const getFileExtensionFromUrl = (url = "") => {
	const sanitizedUrl = url.split("?")[0];
	const match = sanitizedUrl.match(/\.([a-zA-Z0-9]+)$/);
	return match?.[1]?.toLowerCase() || "jpg";
};

const triggerDirectDownload = (url, filename) => {
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = filename;
	anchor.rel = "noopener";
	anchor.target = "_blank";
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
};

const triggerBlobDownload = (blob, filename) => {
	const blobUrl = window.URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = blobUrl;
	anchor.download = filename;
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	window.URL.revokeObjectURL(blobUrl);
};

const getChannelsForSku = (sku) => {
	const nextChannels = new Set([fallbackChannel]);
	Object.keys(sku?.channel_counts ?? {}).forEach((channel) => nextChannels.add(channel));
	return Array.from(nextChannels);
};

const buildChannelDeltasFromImages = (images, delta = 1) =>
	images.reduce((acc, image) => {
		const channel = getImageChannel(image);
		acc[channel] = (acc[channel] ?? 0) + delta;
		return acc;
	}, {});

const applySummaryDelta = (sku, { imageDelta = 0, channelDeltas = {} }) => {
	const nextChannelCounts = { ...(sku?.channel_counts ?? {}) };
	Object.entries(channelDeltas).forEach(([channel, delta]) => {
		const nextCount = Math.max(0, (nextChannelCounts[channel] ?? 0) + delta);
		if (nextCount === 0) delete nextChannelCounts[channel];
		else nextChannelCounts[channel] = nextCount;
	});

	return {
		...sku,
		image_count: Math.max(0, (sku?.image_count ?? 0) + imageDelta),
		channel_counts: nextChannelCounts,
	};
};

const updateSkusSummaryFromUpload = (skus, uploadedRows) =>
	skus.map((sku) => {
		const rowsForSku = uploadedRows.filter((row) => row.shoot_sku_id === sku.id);
		if (!rowsForSku.length) return sku;
		return applySummaryDelta(sku, {
			imageDelta: rowsForSku.length,
			channelDeltas: buildChannelDeltasFromImages(rowsForSku),
		});
	});

const getExistingShootChannelCounts = (shootSkus = []) => {
	const counts = {};
	shootSkus.forEach((sku) => {
		Object.entries(sku?.channel_counts ?? {}).forEach(([channel, count]) => {
			counts[channel] = (counts[channel] ?? 0) + (Number(count) || 0);
		});
	});
	return counts;
};

const createEmptySkippedDetails = () => ({
	invalidStructure: { count: 0, items: [] },
	invalidChannel: { count: 0, items: [] },
	unknownSku: { count: 0, items: [] },
	duplicateTarget: { count: 0, items: [] },
});

const pushUniqueSkippedItem = (bucket, item, limit = 25) => {
	bucket.count += 1;
	if (bucket.items.length >= limit) return;
	if (bucket.items.includes(item)) return;
	bucket.items.push(item);
};

const parseShootLevelFileEntry = (file, skuMap, targetPathSet, skippedDetails) => {
	const relativePath = file.webkitRelativePath || file.name;
	const pathParts = relativePath.split("/").filter(Boolean);
	if (pathParts.length < 4) {
		pushUniqueSkippedItem(
			skippedDetails.invalidStructure,
			`${relativePath} (expected shoot/channel/sku/image)`,
		);
		return null;
	}

	const relativeParts = pathParts.slice(1);
	const [channelFolder, skuFolder] = relativeParts;
	const channel = normalizeKey(channelFolder);
	if (!allowedChannelValues.has(channel)) {
		pushUniqueSkippedItem(
			skippedDetails.invalidChannel,
			`Channel "${channelFolder || "-"}" is not allowed (file: ${relativePath})`,
		);
		return null;
	}

	const matchedSku = skuMap.get(normalizeKey(skuFolder));
	if (!matchedSku?.id) {
		pushUniqueSkippedItem(
			skippedDetails.unknownSku,
			`SKU "${skuFolder || "-"}" is not in this shoot (channel: ${channelFolder}, file: ${file.name})`,
		);
		return null;
	}

	const targetKey = `${matchedSku.id}::${channel}::${file.name}`;
	if (targetPathSet.has(targetKey)) {
		pushUniqueSkippedItem(
			skippedDetails.duplicateTarget,
			`Duplicate file "${file.name}" for SKU ${matchedSku.i_code} in ${channel}`,
		);
		return null;
	}
	targetPathSet.add(targetKey);

	return {
		file,
		channel,
		shoot_sku_id: matchedSku.id,
		i_code: matchedSku.i_code,
	};
};

const buildShootLevelChannelPreview = (validFiles, shootSkus) => {
	const skuMap = new Map(shootSkus.map((sku) => [normalizeKey(sku?.i_code), sku]));
	const skippedDetails = createEmptySkippedDetails();
	const targetPathSet = new Set();
	const channelMap = new Map();
	const existingChannelCounts = getExistingShootChannelCounts(shootSkus);

	validFiles.forEach((file) => {
		const entry = parseShootLevelFileEntry(file, skuMap, targetPathSet, skippedDetails);
		if (!entry) return;

		if (!channelMap.has(entry.channel)) {
			channelMap.set(entry.channel, { channel: entry.channel, entries: [], skuCodes: new Set() });
		}

		const group = channelMap.get(entry.channel);
		group.entries.push(entry);
		group.skuCodes.add(entry.i_code);
	});

	const previewData = Array.from(channelMap.values())
		.map((group) => ({
			channel: group.channel,
			channelLabel: formatLabel(group.channel),
			fileCount: group.entries.length,
			skuCount: group.skuCodes.size,
			existingCount: existingChannelCounts[group.channel] ?? 0,
			entries: group.entries,
		}))
		.sort((a, b) => a.channel.localeCompare(b.channel));

	const skippedSummary = {
		invalidStructure: skippedDetails.invalidStructure.count,
		invalidChannel: skippedDetails.invalidChannel.count,
		unknownSku: skippedDetails.unknownSku.count,
		duplicateTarget: skippedDetails.duplicateTarget.count,
		totalSkipped:
			skippedDetails.invalidStructure.count +
			skippedDetails.invalidChannel.count +
			skippedDetails.unknownSku.count +
			skippedDetails.duplicateTarget.count,
	};

	return { previewData, skippedSummary, skippedDetails };
};

const SHOOT_URL_BATCH_SIZE = 50;
const SHOOT_UPLOAD_CONCURRENCY = 6;

const formatBytes = (bytes = 0) => {
	if (!bytes || bytes < 1024) return `${bytes || 0} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const uploadFileToResumableUrl = (uploadUrl, file, contentType, onProgress = () => {}) =>
	new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open("PUT", uploadUrl);
		xhr.setRequestHeader("Content-Type", contentType || file.type || "application/octet-stream");

		xhr.upload.onprogress = (event) => {
			if (!event.lengthComputable) return;
			onProgress(event.loaded, event.total || file.size || 0);
		};

		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				onProgress(file.size || 0, file.size || 0);
				resolve();
				return;
			}
			reject(new Error(`Upload failed for ${file.name}: ${xhr.status} ${xhr.responseText || ""}`.trim()));
		};

		xhr.onerror = () => reject(new Error(`Network error while uploading ${file.name}`));
		xhr.send(file);
	});

const fetchShootUploadUrls = async (entries) => {
	const urlMap = new Map();
	const batches = [];

	for (let i = 0; i < entries.length; i += SHOOT_URL_BATCH_SIZE) {
		batches.push(entries.slice(i, i + SHOOT_URL_BATCH_SIZE));
	}

	for (const batch of batches) {
		const signedRows = await axios
			.post(
				"/api/studio/shoot_sku_upload_signed_urls",
				batch.map((entry) => ({
					fileName: entry.file.name,
					contentType: entry.file.type || "application/octet-stream",
					shoot_sku_id: entry.shoot_sku_id,
					channel: entry.channel,
				})),
			)
			.then((res) => res.data.result ?? []);

		signedRows.forEach((row) => {
			urlMap.set(`${row.shoot_sku_id}::${row.channel}::${row.fileName}`, row);
		});
	}

	return urlMap;
};

const uploadShootChannelBatch = async (entries, onProgress = () => {}) => {
	const totalFiles = entries.length;
	const totalBytes = entries.reduce((sum, entry) => sum + (entry.file.size || 0), 0);
	const fileProgress = new Map(entries.map((entry) => [`${entry.shoot_sku_id}::${entry.channel}::${entry.file.name}`, 0]));

	const emitProgress = ({
		phase = "uploading",
		completedFiles = 0,
		failedFiles = 0,
		currentFile = "",
		forcePercent,
	} = {}) => {
		const uploadedBytes = Array.from(fileProgress.values()).reduce((sum, value) => sum + value, 0);
		const percent =
			typeof forcePercent === "number"
				? forcePercent
				: totalBytes > 0
					? Math.min(99, Math.round((uploadedBytes / totalBytes) * 100))
					: Math.round((completedFiles / Math.max(totalFiles, 1)) * 100);

		onProgress({
			phase,
			percent,
			completedFiles,
			failedFiles,
			totalFiles,
			uploadedBytes,
			totalBytes,
			currentFile,
		});
	};

	emitProgress({ phase: "preparing", forcePercent: 0 });

	const urlMap = await fetchShootUploadUrls(entries);
	emitProgress({ phase: "uploading", forcePercent: 1, currentFile: "Starting uploads..." });

	const queue = entries.map((entry) => {
		const uploadKey = `${entry.shoot_sku_id}::${entry.channel}::${entry.file.name}`;
		const uploadRow = urlMap.get(uploadKey);
		if (!uploadRow?.uploadUrl && !uploadRow?.signedUrl) {
			throw new Error(`Missing resumable upload URL for ${entry.file.name}`);
		}
		return { entry, uploadRow, uploadKey };
	});

	const successfulRows = [];
	const failedEntries = [];
	let completedFiles = 0;

	const workers = Array.from({ length: Math.min(SHOOT_UPLOAD_CONCURRENCY, queue.length) }, async () => {
		while (queue.length) {
			const next = queue.shift();
			if (!next) return;

			const { entry, uploadRow, uploadKey } = next;
			try {
				await uploadFileToResumableUrl(
					uploadRow.uploadUrl || uploadRow.signedUrl,
					entry.file,
					uploadRow.contentType || entry.file.type,
					(loaded) => {
						fileProgress.set(uploadKey, loaded);
						emitProgress({
							phase: "uploading",
							completedFiles,
							failedFiles: failedEntries.length,
							currentFile: entry.file.name,
						});
					},
				);
				fileProgress.set(uploadKey, entry.file.size || 0);
				successfulRows.push(uploadRow);
				completedFiles += 1;
				emitProgress({
					phase: "uploading",
					completedFiles,
					failedFiles: failedEntries.length,
					currentFile: entry.file.name,
				});
			} catch (error) {
				failedEntries.push({
					...entry,
					errorMessage: error?.message || "Upload failed",
				});
				emitProgress({
					phase: "uploading",
					completedFiles,
					failedFiles: failedEntries.length,
					currentFile: entry.file.name,
				});
			}
		}
	});

	await Promise.all(workers);

	if (!successfulRows.length) {
		throw new Error(
			failedEntries[0]?.errorMessage ||
				`All ${totalFiles} file uploads failed for this channel`,
		);
	}

	emitProgress({
		phase: "saving",
		completedFiles,
		failedFiles: failedEntries.length,
		currentFile: "Saving channel images to database...",
		forcePercent: 99,
	});

	const sqlPayload = successfulRows.map((row) => ({
		i_code: row.i_code,
		channel: row.channel,
		is_main: false,
		url: row.publicUrl,
		file_path: row.filePath,
		shoot_sku_id: row.shoot_sku_id,
	}));

	const savedRows = await axios
		.post("/api/studio/bulk_create_shoot_sku_images_sql", sqlPayload)
		.then((res) => res.data.result);

	emitProgress({
		phase: "done",
		completedFiles: savedRows.length,
		failedFiles: failedEntries.length,
		currentFile: "",
		forcePercent: 100,
	});

	return {
		savedRows,
		failedEntries,
	};
};

const ImageBox = ({ data, handleDelete = () => {}, disableButtons = false }) => {
	return (
		<Box sx={{ position: "relative" }}>
			<img
				alt=""
				src={data?.url}
				style={{
					width: "100%",
					height: 180,
					objectFit: "cover",
					objectPosition: "50% 0%",
					borderRadius: 12,
					display: "block",
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					right: 10,
					top: 10,
					display: disableButtons ? "none" : "block",
				}}>
				<IconButton
					disabled={disableButtons}
					onClick={() => handleDelete(data)}
					sx={{ background: "#11111199", "&:hover": { background: "#111111cc" } }}>
					<DeleteRounded sx={{ color: "#fff" }} />
				</IconButton>
			</Box>
		</Box>
	);
};

const ShootSkuImages = ({ shootSkus, setShootSkus, handleAddSkuClick = () => {}, hideAddSku = false, disableEdit = false }) => {
	const { setSnackBar, setSnackBarOpen, snackBar, setLoadingOpen } = GlobalState();

	const [addViewImgsModal, setAddViewImgsModal] = useState(false);
	const [structureEditModal, setStructureEditModal] = useState(false);
	const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);

	const [selectedSku, setSelectedSku] = useState();
	const [selectedSkuImages, setSelectedSkuImages] = useState([]);
	const [isSkuImagesLoading, setIsSkuImagesLoading] = useState(false);
	const [selectedChannel, setSelectedChannel] = useState(fallbackChannel);
	const [selectedImgModalChannel, setSelectedImgModalChannel] = useState(fallbackChannel);
	const [channels, setChannels] = useState([fallbackChannel]);
	const [newChannelState, setNewChannelState] = useState("MYNTRA");
	const [imgToDelState, setImgToDelState] = useState();
	const [shootFolderUploadModal, setShootFolderUploadModal] = useState(false);
	const [shootLevelPreviewModal, setShootLevelPreviewModal] = useState(false);
	const [shootLevelPreviewData, setShootLevelPreviewData] = useState([]);
	const [shootLevelSkippedDetails, setShootLevelSkippedDetails] = useState(createEmptySkippedDetails());
	const [selectedShootChannels, setSelectedShootChannels] = useState([]);
	const [shootLevelUploadProgress, setShootLevelUploadProgress] = useState(0);
	const [isShootLevelUploading, setIsShootLevelUploading] = useState(false);
	const [shootUploadStatus, setShootUploadStatus] = useState({
		currentChannel: "",
		channelIndex: 0,
		totalChannels: 0,
		channelProgress: 0,
		phase: "",
		completedFiles: 0,
		failedFiles: 0,
		totalFiles: 0,
		uploadedBytes: 0,
		totalBytes: 0,
		currentFile: "",
	});
	const folderInputRef = useRef(null);
	const shootFolderInputRef = useRef(null);

	const fetchSkuImages = async (shootSkuId) => {
		setIsSkuImagesLoading(true);
		setSelectedSkuImages([]);
		try {
			const res = await axios.get(`/api/studio/shoot_sku_images/${shootSkuId}`);
			setSelectedSkuImages(res.data.result ?? []);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error), severity: "error" });
			setSelectedSkuImages([]);
		} finally {
			setIsSkuImagesLoading(false);
		}
	};

	useEffect(() => {
		if (!shootSkus?.length) {
			setSelectedSku(null);
			return;
		}

		setSelectedSku((prev) => {
			if (prev && shootSkus.some((row) => row.id === prev.id)) {
				return shootSkus.find((row) => row.id === prev.id) ?? shootSkus[0];
			}
			return shootSkus[0];
		});
	}, [shootSkus]);

	useEffect(() => {
		if (!selectedSku?.id) {
			setSelectedSkuImages([]);
			return;
		}
		fetchSkuImages(selectedSku.id);
	}, [selectedSku?.id]);

	useEffect(() => {
		if (!selectedSku) {
			setChannels([fallbackChannel]);
			setSelectedChannel(fallbackChannel);
			return;
		}

		const nextChannels = getChannelsForSku(selectedSku);
		setChannels(nextChannels);
		setSelectedChannel((prev) => (prev && nextChannels.includes(prev) ? prev : nextChannels[0] || fallbackChannel));
	}, [selectedSku?.id, selectedSku?.channel_counts]);

	const channelSummary = useMemo(() => {
		return channels.map((channel) => {
			if (!isSkuImagesLoading) {
				const loadedCount = selectedSkuImages.filter((image) => getImageChannel(image) === channel).length;
				return { channel, imageCount: loadedCount };
			}
			return { channel, imageCount: selectedSku?.channel_counts?.[channel] ?? 0 };
		});
	}, [channels, selectedSkuImages, selectedSku, isSkuImagesLoading]);

	const handleSkuSelect = (sku) => {
		setSelectedSku(shootSkus?.find((row) => row?.i_code === sku.i_code) ?? null);
	};

	const handleStructureModalClose = () => {
		setStructureEditModal(false);
		setNewChannelState("MYNTRA");
	};

	const handleDeleteConfirmModalClose = () => {
		setDeleteConfirmModal(false);
	};

	const handleAddImagesClick = (channel) => {
		setSelectedImgModalChannel(channel);
		setAddViewImgsModal(true);
	};

	const addNewShootSkuImages = async (images, channel) => {
		const formData = new FormData();
		for (let i = 0; i < images.length; i++) {
			formData.append(shootSkuImagesAssoc, images[i].file);
		}
		formData.append("shoot_sku_id", String(selectedSku?.id ?? ""));
		formData.append("channel", channel);

		const imageUrls = await axios
			.post("/api/studio/upload_shoot_sku_images", formData, {
				headers: {
					"content-type": "multipart/form-data",
				},
			})
			.then((res) => res.data.result);

		const productImages = imageUrls.map((row) => {
			return {
				i_code: row.i_code ?? selectedSku?.i_code,
				channel,
				is_main: false,
				url: row.publicUrl,
				file_path: row.filePath,
				shoot_sku_id: selectedSku?.id,
			};
		});

		try {
			const res = await axios.post("/api/studio/bulk_create_shoot_sku_images_sql", productImages);
			return res.data.result;
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			setLoadingOpen(false);
			return false;
		}
	};

	const handleSaveImagesClick = async (images) => {
		if (!images.length) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "Please select atleast one image", severity: "error" });
			return;
		}

		try {
			setLoadingOpen(true);
			const finalData = await addNewShootSkuImages(images, selectedImgModalChannel);
			if (!finalData) return;

			setSelectedSkuImages((prev) => [...prev, ...finalData]);
			const summaryUpdate = {
				imageDelta: finalData.length,
				channelDeltas: buildChannelDeltasFromImages(finalData),
			};
			setShootSkus((prev) => prev.map((sku) => (sku?.id === selectedSku?.id ? applySummaryDelta(sku, summaryUpdate) : sku)));
			setSelectedSku((prev) => applySummaryDelta(prev, summaryUpdate));

			setChannels((prev) => (prev.includes(selectedImgModalChannel) ? prev : [...prev, selectedImgModalChannel]));

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Images added successfully",
				severity: "success",
			});
			setAddViewImgsModal(false);
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error), severity: "error" });
			setLoadingOpen(false);
		}
	};

	const handleBulkFolderButtonClick = () => {
		folderInputRef.current?.click();
	};

	const handleShootFolderButtonClick = () => {
		if (!shootSkus?.length) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "Please add SKUs to this shoot first", severity: "error" });
			return;
		}
		setShootFolderUploadModal(true);
	};

	const handleShootFolderUploadModalClose = () => {
		setShootFolderUploadModal(false);
	};

	const handleChooseShootFolderClick = () => {
		shootFolderInputRef.current?.click();
	};

	const handleShootLevelPreviewClose = () => {
		if (isShootLevelUploading) return;
		setShootLevelPreviewModal(false);
		setShootLevelPreviewData([]);
		setShootLevelSkippedDetails(createEmptySkippedDetails());
		setSelectedShootChannels([]);
		setShootLevelUploadProgress(0);
		setShootUploadStatus({
			currentChannel: "",
			channelIndex: 0,
			totalChannels: 0,
			channelProgress: 0,
			phase: "",
			completedFiles: 0,
			failedFiles: 0,
			totalFiles: 0,
			uploadedBytes: 0,
			totalBytes: 0,
			currentFile: "",
		});
	};

	const handleBulkFolderUpload = async (event) => {
		const rawFiles = Array.from(event.target.files ?? []);
		event.target.value = null;

		if (!selectedSku) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "Please select a SKU first", severity: "error" });
			return;
		}

		const validFiles = rawFiles.filter(isAllowedImageFile);
		if (!validFiles.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `No supported images found. Allowed types: ${uploadImageFileTypes.join(", ")}`,
				severity: "error",
			});
			return;
		}

		const parsedEntries = [];
		let invalidChannelCount = 0;
		for (const file of validFiles) {
			const pathParts = (file.webkitRelativePath || file.name).split("/").filter(Boolean);
			const relativeParts = pathParts.length > 1 ? pathParts.slice(1) : pathParts;

			let channel = selectedChannel || fallbackChannel;

			if (relativeParts.length >= 2) {
				channel = normalizeKey(relativeParts[0]);
				if (!allowedChannelValues.has(channel)) {
					invalidChannelCount += 1;
					continue;
				}
			}

			parsedEntries.push({
				file,
				channel,
			});
		}

		if (invalidChannelCount > 0) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `${invalidChannelCount} files were skipped because they belong to an unknown channel folder. Allowed channels are: ${availableChannels.map((c) => c.label).join(", ")}`,
				severity: "warning",
			});
			return;
		}

		if (!parsedEntries.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Folder format should be channel/image inside the selected channel folder or directly images",
				severity: "error",
			});
			return;
		}

		try {
			setLoadingOpen(true);
			const formData = new FormData();
			parsedEntries.forEach((entry) => {
				formData.append(shootSkuImagesAssoc, entry.file);
			});
			formData.append(
				"uploadMeta",
				JSON.stringify(
					parsedEntries.map((entry) => ({
						fileName: entry.file.name,
						shoot_sku_id: selectedSku?.id,
						channel: entry.channel,
					})),
				),
			);

			const imageUrls = await axios
				.post("/api/studio/upload_shoot_sku_images", formData, {
					headers: {
						"content-type": "multipart/form-data",
					},
				})
				.then((res) => res.data.result);

			const sqlPayload = imageUrls.map((row, index) => ({
				i_code: row.i_code ?? selectedSku?.i_code,
				channel: row.channel ?? parsedEntries[index].channel,
				is_main: false,
				url: row.publicUrl,
				file_path: row.filePath,
				shoot_sku_id: selectedSku?.id,
			}));

			const finalData = await axios
				.post("/api/studio/bulk_create_shoot_sku_images_sql", sqlPayload)
				.then((res) => res.data.result);

			const nextChannels = new Set(channels);
			parsedEntries.forEach(({ channel }) => {
				nextChannels.add(channel);
			});

			setChannels(Array.from(nextChannels));
			setSelectedSkuImages((prev) => [...prev, ...finalData]);
			const summaryUpdate = {
				imageDelta: finalData.length,
				channelDeltas: buildChannelDeltasFromImages(finalData),
			};
			setShootSkus((prev) => prev.map((sku) => (sku?.id === selectedSku?.id ? applySummaryDelta(sku, summaryUpdate) : sku)));
			setSelectedSku((prev) => applySummaryDelta(prev, summaryUpdate));

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `${finalData.length} images uploaded successfully`,
				severity: "success",
			});
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
			});
			setLoadingOpen(false);
		}
	};

	const handleShootLevelBulkFolderUpload = async (event) => {
		const rawFiles = Array.from(event.target.files ?? []);
		event.target.value = null;

		if (!shootSkus?.length) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "Please add SKUs to this shoot first", severity: "error" });
			return;
		}

		const validFiles = rawFiles.filter(isAllowedImageFile);
		if (!validFiles.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `No supported images found. Allowed types: ${uploadImageFileTypes.join(", ")}`,
				severity: "error",
			});
			return;
		}

		const { previewData, skippedSummary, skippedDetails } = buildShootLevelChannelPreview(validFiles, shootSkus);

		setShootFolderUploadModal(false);
		setShootLevelPreviewData(previewData);
		setShootLevelSkippedDetails(skippedDetails);
		setSelectedShootChannels(previewData.map((item) => item.channel));
		setShootLevelPreviewModal(true);

		if (!previewData.length && !skippedSummary.totalSkipped) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "No valid channel/SKU folders were found in the selected folder",
				severity: "error",
			});
		}
	};

	const handleConfirmShootLevelUpload = async () => {
		const selectedChannelSet = new Set(selectedShootChannels);
		const selectedPreviewRows = shootLevelPreviewData.filter((row) => selectedChannelSet.has(row.channel));
		if (!selectedPreviewRows.length) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "Please select at least one channel to upload", severity: "error" });
			return;
		}

		const currentSelectedSkuId = selectedSku?.id;
		const allFinalData = [];
		const failedChannels = [];
		const partialFailedChannels = [];

		try {
			setIsShootLevelUploading(true);
			setShootLevelUploadProgress(0);
			setShootUploadStatus({
				currentChannel: selectedPreviewRows[0]?.channelLabel ?? "",
				channelIndex: 0,
				totalChannels: selectedPreviewRows.length,
				channelProgress: 0,
				phase: "preparing",
				completedFiles: 0,
				failedFiles: 0,
				totalFiles: selectedPreviewRows.reduce((sum, row) => sum + row.entries.length, 0),
				uploadedBytes: 0,
				totalBytes: 0,
				currentFile: "",
			});

			for (let index = 0; index < selectedPreviewRows.length; index += 1) {
				const channelRow = selectedPreviewRows[index];
				setShootUploadStatus((prev) => ({
					...prev,
					currentChannel: channelRow.channelLabel,
					channelIndex: index + 1,
					totalChannels: selectedPreviewRows.length,
					channelProgress: 0,
					phase: "preparing",
					completedFiles: 0,
					failedFiles: 0,
					totalFiles: channelRow.entries.length,
					uploadedBytes: 0,
					totalBytes: channelRow.entries.reduce((sum, entry) => sum + (entry.file.size || 0), 0),
					currentFile: "",
				}));

				try {
					const { savedRows, failedEntries } = await uploadShootChannelBatch(channelRow.entries, (progress) => {
						setShootUploadStatus((prev) => ({
							...prev,
							channelProgress: progress.percent,
							phase: progress.phase,
							completedFiles: progress.completedFiles,
							failedFiles: progress.failedFiles,
							totalFiles: progress.totalFiles,
							uploadedBytes: progress.uploadedBytes,
							totalBytes: progress.totalBytes,
							currentFile: progress.currentFile,
						}));
						setShootLevelUploadProgress(
							Math.round(((index + progress.percent / 100) / selectedPreviewRows.length) * 100),
						);
					});

					allFinalData.push(...savedRows);
					setShootSkus((prev) => updateSkusSummaryFromUpload(prev, savedRows));
					setSelectedSku((prev) => {
						if (!prev) return prev;
						return updateSkusSummaryFromUpload([prev], savedRows)[0];
					});

					if (failedEntries.length) {
						partialFailedChannels.push({
							channel: channelRow.channel,
							channelLabel: channelRow.channelLabel,
							existingCount: channelRow.existingCount + savedRows.length,
							entries: failedEntries,
							fileCount: failedEntries.length,
							skuCount: new Set(failedEntries.map((entry) => entry.i_code)).size,
						});
					}
				} catch (error) {
					failedChannels.push({
						channel: channelRow.channelLabel,
						channelKey: channelRow.channel,
						message: parseErrorMessage(error),
						row: channelRow,
					});
				}
			}

			if (allFinalData.some((row) => row.shoot_sku_id === currentSelectedSkuId) && currentSelectedSkuId) {
				await fetchSkuImages(currentSelectedSkuId);
			} else if (selectedSku) {
				setChannels(getChannelsForSku(selectedSku));
			}

			if (!allFinalData.length && failedChannels.length && !partialFailedChannels.length) {
				throw new Error(failedChannels.map((row) => `${row.channel}: ${row.message}`).join(" | "));
			}

			const remainingPreview = [
				...partialFailedChannels,
				...failedChannels.map((row) => row.row).filter(Boolean),
			];

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: remainingPreview.length
					? `${allFinalData.length} images uploaded. ${remainingPreview.length} channel(s) still need retry.`
					: `${allFinalData.length} images uploaded successfully across ${selectedPreviewRows.length} channel(s)`,
				severity: remainingPreview.length ? "warning" : "success",
			});

			if (remainingPreview.length) {
				setShootLevelPreviewData(remainingPreview);
				setSelectedShootChannels(remainingPreview.map((row) => row.channel));
			} else {
				handleShootLevelPreviewClose();
			}
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
			});
		} finally {
			setIsShootLevelUploading(false);
			setShootLevelUploadProgress((prev) => (prev === 100 ? 100 : prev));
			setShootUploadStatus((prev) => ({
				...prev,
				phase: "",
				currentFile: "",
			}));
		}
	};

	/* const handleSkuDownload = async () => {
		if (!selectedSkuImages.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "No images available for download",
				severity: "warning",
			});
			return;
		}

		try {
			setLoadingOpen(true);
			for (let index = 0; index < selectedSkuImages.length; index += 1) {
				const image = selectedSkuImages[index];
				const channel = getImageChannel(image);
				const extension = getFileExtensionFromUrl(image?.url);
				const fileName = `${selectedSku?.i_code || "sku"}_${channel}_${index + 1}.${extension}`;

				try {
					const response = await fetch(image?.url, { mode: "cors" });
					if (!response.ok) throw new Error("Blob download failed");
					const blob = await response.blob();
					triggerBlobDownload(blob, fileName);
				} catch (downloadError) {
					triggerDirectDownload(image?.url, fileName);
				}

				await new Promise((resolve) => setTimeout(resolve, 120));
			}

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Started SKU image downloads. Browser may save them individually instead of in folders.",
				severity: "success",
			});
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
			});
			setLoadingOpen(false);
		}
	}; */

	const handleChannelDelete = (channel) => {
		if (channel === fallbackChannel) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Default channel cannot be removed",
				severity: "error",
			});
			return;
		}

		if (selectedSkuImages.some((row) => getImageChannel(row) === channel)) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please remove images from this channel before removing it",
				severity: "error",
			});
			return;
		}

		setChannels((prev) => prev.filter((c) => c !== channel));
		if (selectedChannel === channel) setSelectedChannel(fallbackChannel);
	};

	const handleSkuImageDeleteClick = (image) => {
		setImgToDelState(image);
		setDeleteConfirmModal(true);
	};

	const handleCancelSkuImageDeleteClick = () => {
		setImgToDelState();
		handleDeleteConfirmModalClose();
	};

	const handleSkuImageDelete = async (e) => {
		e.preventDefault();
		setLoadingOpen(true);

		try {
			await axios.delete(`/api/studio/delete_shoot_sku_image/${imgToDelState.id}`);

			const deletedChannel = getImageChannel(imgToDelState);
			const summaryUpdate = { imageDelta: -1, channelDeltas: { [deletedChannel]: -1 } };
			setSelectedSkuImages((prev) => prev.filter((row) => row?.id !== imgToDelState.id));
			setShootSkus((prev) => prev.map((sku) => (sku?.id === selectedSku?.id ? applySummaryDelta(sku, summaryUpdate) : sku)));
			setSelectedSku((prev) => applySummaryDelta(prev, summaryUpdate));

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Image deleted successfully",
				severity: "success",
			});
			handleDeleteConfirmModalClose();
			setImgToDelState();
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error), severity: "error" });
			setLoadingOpen(false);
		}
	};

	return (
		<Box width="100%" maxWidth="100%" overflow="hidden" display="flex" flexWrap="wrap">
			<Box
				width="100%"
				display="flex"
				justifyContent="space-between"
				alignItems="flex-start"
				pt={1}
				pb={2}
				gap={2}
				flexWrap="wrap">
				<Box>
					<Typography variant="h5">SKU Image Library</Typography>
					<Typography variant="body2" color="text.secondary">
						Organize images by channel.
					</Typography>
				</Box>
				<Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
					<Button
						variant="outlined"
						size="small"
						startIcon={<DriveFolderUploadRounded />}
						onClick={handleShootFolderButtonClick}
						disabled={disableEdit || !shootSkus?.length}>
						Shoot Folder Upload
					</Button>
					<Button
						variant="contained"
						size="small"
						onClick={handleAddSkuClick}
						sx={{ display: hideAddSku ? "none" : "block" }}
						disabled={disableEdit}>
						Add SKU
					</Button>
				</Stack>
			</Box>
			<input
				ref={folderInputRef}
				type="file"
				hidden
				multiple
				accept={uploadImageFileTypes.map((type) => `.${type}`).join(",")}
				onChange={handleBulkFolderUpload}
				webkitdirectory=""
				directory=""
			/>
			<input
				ref={shootFolderInputRef}
				type="file"
				hidden
				multiple
				accept={uploadImageFileTypes.map((type) => `.${type}`).join(",")}
				onChange={handleShootLevelBulkFolderUpload}
				webkitdirectory=""
				directory=""
			/>

			<Box display="flex" width="100%" minHeight="60vh" gap={2} flexWrap="wrap" overflow="hidden" maxHeight="85vh">
				<Paper
					elevation={0}
					sx={{
						width: { xs: "100%", lg: 280 },
						flexShrink: 0,
						maxWidth: "100%",
						border: `1px solid ${grey[200]}`,
						borderRadius: 3,
						p: 1.5,
						background: grey[50],
						height: "100%",
						overflowY: "auto",
					}}>
					<Typography variant="overline" color="text.secondary">
						Skus
					</Typography>
					<List sx={{ pt: 0.5 }}>
						{shootSkus?.map((row) => (
							<ListItem key={row?.i_code} disablePadding sx={{ mb: 0.75 }}>
								<ListItemButton
									onClick={() => handleSkuSelect(row)}
									selected={selectedSku?.i_code === row?.i_code}
									sx={{
										borderRadius: 2,
										border: `1px solid ${selectedSku?.i_code === row?.i_code ? grey[900] : grey[200]}`,
										background: selectedSku?.i_code === row?.i_code ? grey[100] : "transparent",
									}}>
									<ListItemText
										primary={row?.i_code}
										secondary={`${row?.image_count ?? 0} images`}
										primaryTypographyProps={{ fontWeight: 600 }}
									/>
								</ListItemButton>
							</ListItem>
						))}
					</List>
				</Paper>

				<Box flex={1} minWidth={0} maxWidth="100%" maxHeight="100%" overflow="hidden" sx={{ overflowY: "auto" }}>
					<Paper
						elevation={0}
						sx={{
							border: `1px solid ${grey[200]}`,
							borderRadius: 3,
							p: 2,
							minHeight: "100%",
							background:
								"linear-gradient(180deg, rgba(245,245,245,0.55) 0%, rgba(255,255,255,1) 30%, rgba(255,255,255,1) 100%)",
							boxSizing: "border-box",
						}}>
						<Box
							display="flex"
							justifyContent="space-between"
							alignItems="flex-start"
							gap={2}
							flexWrap={{ xs: "wrap", lg: "nowrap" }}
							mb={2}>
							<Box minWidth={0} flex={1}>
								<Typography variant="h6">
									{selectedSku?.i_code ? `${selectedSku.i_code} channels` : "Channels"}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									Each channel contains its own image collection.
								</Typography>
								<Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
									SKU folder format: `channel/image.jpg` or `image.jpg` inside the selected channel.
								</Typography>
							</Box>
							<Stack
								direction="row"
								spacing={1}
								flexWrap={{ xs: "wrap", sm: "nowrap" }}
								justifyContent="flex-end"
								alignItems="flex-start"
								sx={{ ml: "auto" }}>
								{/* <Button
									variant="outlined"
									startIcon={<DownloadRounded />}
									onClick={handleSkuDownload}
									disabled={!selectedSku || !selectedSkuImages.length}>
									Download SKU
								</Button> */}
								<Button
									variant="outlined"
									startIcon={<DriveFolderUploadRounded />}
									onClick={handleBulkFolderButtonClick}
									disabled={disableEdit || !selectedSku}>
									SKU Folder Upload
								</Button>
							</Stack>
						</Box>

						<Box display="flex" flexWrap="wrap" gap={1} mb={3}>
							{channelSummary.map(({ channel, imageCount }) => (
								<Chip
									key={channel}
									label={`${formatLabel(channel)} (${imageCount})`}
									onClick={() => setSelectedChannel(channel)}
									variant={selectedChannel === channel ? "filled" : "outlined"}
									sx={{
										height: 36,
										textTransform: "capitalize",
										backgroundColor: selectedChannel === channel ? grey[900] : "transparent",
										color: selectedChannel === channel ? "#fff" : grey[900],
										borderColor: grey[400],
									}}
								/>
							))}
							<IconButton
								size="small"
								onClick={() => setStructureEditModal(true)}
								disabled={disableEdit || !selectedSku}
								sx={{
									border: `1px solid ${grey[300]}`,
									borderRadius: 2,
									width: 36,
									height: 36,
								}}>
								<EditRounded fontSize="small" />
							</IconButton>
						</Box>

						<Box display="block" maxWidth="100%">
							{selectedSku &&
								(() => {
									const images = selectedSkuImages.filter((row) => getImageChannel(row) === selectedChannel);

									return (
										<Paper
											key={selectedChannel}
											elevation={0}
											sx={{
												border: `1px solid ${grey[200]}`,
												borderRadius: 3,
												p: 1.5,
												background: "#fff",
												minWidth: 0,
												overflow: "hidden",
												gridColumn: "1 / -1",
											}}>
											<Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
												<Box>
													<Typography variant="subtitle1" fontWeight={700} textTransform="capitalize">
														{formatLabel(selectedChannel)}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{images.length} image{images.length === 1 ? "" : "s"}
													</Typography>
												</Box>
												<Button
													variant="contained"
													size="small"
													startIcon={<AddRounded />}
													onClick={() => handleAddImagesClick(selectedChannel)}
													disabled={disableEdit}>
													Add Images
												</Button>
											</Box>

											{isSkuImagesLoading ? (
												<Box
													height={180}
													display="flex"
													alignItems="center"
													justifyContent="center"
													flexDirection="column"
													gap={1}>
													<CircularProgress size={28} />
													<Typography variant="body2" color="text.secondary">
														Loading images...
													</Typography>
												</Box>
											) : images.length ? (
												<Box
													display="grid"
													gridTemplateColumns="repeat(auto-fill, minmax(140px, 1fr))"
													gap={1.25}
													maxWidth="100%">
													{images.map((row) => (
														<ImageBox
															key={row?.id ?? `${row?.url}-${getImageChannel(row)}`}
															data={row}
															disableButtons={disableEdit}
															handleDelete={handleSkuImageDeleteClick}
														/>
													))}
												</Box>
											) : (
												<Box
													height={180}
													border={`1px dashed ${grey[300]}`}
													borderRadius={3}
													display="flex"
													alignItems="center"
													justifyContent="center"
													color={grey[600]}>
													<Typography variant="body2">No images yet for this channel</Typography>
												</Box>
											)}
										</Paper>
									);
								})()}
						</Box>
					</Paper>
				</Box>
			</Box>

			{addViewImgsModal && (
				<AddViewImagesModal
					modalOpen={addViewImgsModal}
					setModalOpen={setAddViewImgsModal}
					submitFunction={handleSaveImagesClick}
					channel={selectedImgModalChannel}
				/>
			)}

			<Dialog
				fullWidth
				maxWidth="xs"
				open={deleteConfirmModal}
				onClose={handleDeleteConfirmModalClose}
				component="form"
				keepMounted={false}
				onSubmit={handleSkuImageDelete}>
				<Typography p={2} boxSizing="border-box" variant="h6">
					Are you sure you want to delete this image?
				</Typography>
				<Box px={2} width="100%" boxSizing="border-box" mb={2} display="flex" justifyContent="center">
					<Box width={220}>
						<ImageBox data={imgToDelState} disableButtons={true} />
					</Box>
				</Box>
				<DialogActions>
					<Button variant="outlined" onClick={handleCancelSkuImageDeleteClick}>
						Cancel
					</Button>
					<Button autoFocus variant="contained" sx={{ mr: 1 }} type="submit">
						Confirm Delete
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog fullWidth maxWidth="md" open={structureEditModal} onClose={handleStructureModalClose}>
				<Typography width="100%" p={2} pb={1} boxSizing="border-box" variant="h6">
					Manage Channels
				</Typography>
				<Box px={2} pb={2} sx={{ overflowX: "hidden" }}>
					<Typography variant="body2" color="text.secondary" mb={2}>
						Channels group your SKU imagery.
					</Typography>

					<Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3} alignItems={{ xs: "stretch", md: "flex-start" }}>
						<TextField
							select
							label="Add Channel"
							size="small"
							fullWidth
							value={newChannelState}
							onChange={(e) => setNewChannelState(e.target.value)}>
							{availableChannels.map((channel) => (
								<MenuItem key={channel.value} value={channel.value}>
									{channel.label}
								</MenuItem>
							))}
						</TextField>
						<Button
							variant="contained"
							sx={{ width: { xs: "100%", md: "auto" }, flexShrink: 0, minHeight: 40 }}
							onClick={() => {
								const normalizedChannel = normalizeKey(newChannelState);
								if (channels.includes(normalizedChannel)) {
									setSnackBarOpen(true);
									setSnackBar({
										...snackBar,
										message: "Channel already exists",
										severity: "error",
									});
									return;
								}
								setChannels((prev) => [...prev, normalizedChannel]);
								setSelectedChannel(normalizedChannel);
								setNewChannelState("MYNTRA");
							}}>
							Add Channel
						</Button>
					</Stack>

					<Box display="flex" flexWrap="wrap" gap={1} mb={3} maxWidth="100%">
						{channels.map((channel) => (
							<Chip
								key={channel}
								label={formatLabel(channel)}
								onClick={() => setSelectedChannel(channel)}
								onDelete={disableEdit ? undefined : () => handleChannelDelete(channel)}
								disabled={channel === fallbackChannel}
								variant={selectedChannel === channel ? "filled" : "outlined"}
								sx={{ textTransform: "capitalize" }}
							/>
						))}
					</Box>
				</Box>
				<DialogActions>
					<Button variant="outlined" onClick={handleStructureModalClose}>
						Close
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				fullWidth
				maxWidth="sm"
				open={shootFolderUploadModal}
				onClose={handleShootFolderUploadModalClose}
				keepMounted={false}>
				<Typography width="100%" p={2} pb={1} boxSizing="border-box" variant="h6">
					Shoot Folder Upload
				</Typography>
				<Box px={2} pb={2}>
					<Typography variant="body2" color="text.secondary" mb={2}>
						Upload images for multiple SKUs and channels in one go. Choose a folder on your computer that follows the
						structure below.
					</Typography>
					<Paper elevation={0} sx={{ border: `1px solid ${grey[200]}`, borderRadius: 2, p: 2, mb: 2, background: grey[50] }}>
						<Typography variant="subtitle2" fontWeight={600} mb={1}>
							Folder structure
						</Typography>
						<Typography variant="body2" component="div" sx={{ fontFamily: "monospace", mb: 1.5 }}>
							shoot-folder / channel / sku / image.jpg
						</Typography>
						<Typography variant="body2" color="text.secondary" mb={0.5}>
							Example: shoot-1 / MYNTRA / RR1234 / front.jpg
						</Typography>
						<Typography variant="body2" color="text.secondary" mb={1.5}>
							Allowed channels: {availableChannels.map((c) => c.label).join(", ")}
						</Typography>
						<Typography variant="body2" color="text.secondary" mb={0.5}>
							The SKU folder name must match an i_code already added to this shoot.
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Supported images: {uploadImageFileTypes.join(", ")}
						</Typography>
					</Paper>
					<Button
						variant="contained"
						fullWidth
						startIcon={<DriveFolderUploadRounded />}
						onClick={handleChooseShootFolderClick}
						disabled={disableEdit}>
						Choose Folder
					</Button>
				</Box>
				<DialogActions>
					<Button variant="outlined" onClick={handleShootFolderUploadModalClose}>
						Cancel
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				fullWidth
				maxWidth="md"
				open={shootLevelPreviewModal}
				onClose={isShootLevelUploading ? undefined : handleShootLevelPreviewClose}
				keepMounted={false}>
				<Typography width="100%" p={2} pb={1} boxSizing="border-box" variant="h6">
					Shoot Upload Preview
				</Typography>
				<Box px={2} pb={2}>
					<Typography variant="body2" color="text.secondary" mb={2}>
						Select the channel folders to upload. Expected structure inside your selected folder is
						`channel/sku/image.jpg`.
					</Typography>
					{(shootLevelSkippedDetails.unknownSku.count > 0 ||
						shootLevelSkippedDetails.invalidChannel.count > 0 ||
						shootLevelSkippedDetails.invalidStructure.count > 0 ||
						shootLevelSkippedDetails.duplicateTarget.count > 0) && (
						<Alert severity={shootLevelPreviewData.length ? "warning" : "error"} sx={{ mb: 2 }}>
							<Typography variant="subtitle2" fontWeight={700} mb={1}>
								{shootLevelPreviewData.length
									? "Some files/folders were skipped"
									: "No valid uploadable channels found"}
							</Typography>
							{shootLevelSkippedDetails.unknownSku.count > 0 ? (
								<Box mb={1.25}>
									<Typography variant="body2" fontWeight={600}>
										SKU not in this shoot ({shootLevelSkippedDetails.unknownSku.count} files)
									</Typography>
									{shootLevelSkippedDetails.unknownSku.items.map((item) => (
										<Typography key={item} variant="caption" display="block" color="text.secondary">
											• {item}
										</Typography>
									))}
									{shootLevelSkippedDetails.unknownSku.count >
									shootLevelSkippedDetails.unknownSku.items.length ? (
										<Typography variant="caption" display="block" color="text.secondary">
											• ...and{" "}
											{shootLevelSkippedDetails.unknownSku.count -
												shootLevelSkippedDetails.unknownSku.items.length}{" "}
											more
										</Typography>
									) : null}
								</Box>
							) : null}
							{shootLevelSkippedDetails.invalidChannel.count > 0 ? (
								<Box mb={1.25}>
									<Typography variant="body2" fontWeight={600}>
										Invalid channel ({shootLevelSkippedDetails.invalidChannel.count} files)
									</Typography>
									<Typography variant="caption" display="block" color="text.secondary" mb={0.5}>
										Allowed: {availableChannels.map((c) => c.label).join(", ")}
									</Typography>
									{shootLevelSkippedDetails.invalidChannel.items.map((item) => (
										<Typography key={item} variant="caption" display="block" color="text.secondary">
											• {item}
										</Typography>
									))}
									{shootLevelSkippedDetails.invalidChannel.count >
									shootLevelSkippedDetails.invalidChannel.items.length ? (
										<Typography variant="caption" display="block" color="text.secondary">
											• ...and{" "}
											{shootLevelSkippedDetails.invalidChannel.count -
												shootLevelSkippedDetails.invalidChannel.items.length}{" "}
											more
										</Typography>
									) : null}
								</Box>
							) : null}
							{shootLevelSkippedDetails.invalidStructure.count > 0 ? (
								<Box mb={1.25}>
									<Typography variant="body2" fontWeight={600}>
										Invalid folder structure ({shootLevelSkippedDetails.invalidStructure.count} files)
									</Typography>
									<Typography variant="caption" display="block" color="text.secondary" mb={0.5}>
										Expected: shoot-folder / channel / sku / image.jpg
									</Typography>
									{shootLevelSkippedDetails.invalidStructure.items.map((item) => (
										<Typography key={item} variant="caption" display="block" color="text.secondary">
											• {item}
										</Typography>
									))}
									{shootLevelSkippedDetails.invalidStructure.count >
									shootLevelSkippedDetails.invalidStructure.items.length ? (
										<Typography variant="caption" display="block" color="text.secondary">
											• ...and{" "}
											{shootLevelSkippedDetails.invalidStructure.count -
												shootLevelSkippedDetails.invalidStructure.items.length}{" "}
											more
										</Typography>
									) : null}
								</Box>
							) : null}
							{shootLevelSkippedDetails.duplicateTarget.count > 0 ? (
								<Box>
									<Typography variant="body2" fontWeight={600}>
										Duplicate files ({shootLevelSkippedDetails.duplicateTarget.count})
									</Typography>
									{shootLevelSkippedDetails.duplicateTarget.items.map((item) => (
										<Typography key={item} variant="caption" display="block" color="text.secondary">
											• {item}
										</Typography>
									))}
									{shootLevelSkippedDetails.duplicateTarget.count >
									shootLevelSkippedDetails.duplicateTarget.items.length ? (
										<Typography variant="caption" display="block" color="text.secondary">
											• ...and{" "}
											{shootLevelSkippedDetails.duplicateTarget.count -
												shootLevelSkippedDetails.duplicateTarget.items.length}{" "}
											more
										</Typography>
									) : null}
								</Box>
							) : null}
						</Alert>
					)}
					{isShootLevelUploading ? (
						<Box mb={2}>
							<Alert severity="warning" sx={{ mb: 2 }}>
								Upload in progress. Do not close, refresh, or navigate away from this page until all selected
								channels finish uploading.
							</Alert>
							<Typography variant="body2" fontWeight={700} mb={0.5}>
								Overall: {shootLevelUploadProgress}% · Channel {shootUploadStatus.channelIndex}/
								{shootUploadStatus.totalChannels}
							</Typography>
							<LinearProgress variant="determinate" value={shootLevelUploadProgress} sx={{ mb: 1.5 }} />
							<Typography variant="body2" fontWeight={600} mb={0.5} textTransform="capitalize">
								{shootUploadStatus.phase === "preparing"
									? `Preparing ${shootUploadStatus.currentChannel}...`
									: shootUploadStatus.phase === "saving"
										? `Saving ${shootUploadStatus.currentChannel} to database...`
										: `Uploading ${shootUploadStatus.currentChannel}`}
							</Typography>
							<Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
								Files: {shootUploadStatus.completedFiles}/{shootUploadStatus.totalFiles} done
								{shootUploadStatus.failedFiles ? ` · ${shootUploadStatus.failedFiles} failed` : ""}
							</Typography>
							<Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
								Size: {formatBytes(shootUploadStatus.uploadedBytes)} / {formatBytes(shootUploadStatus.totalBytes)}
							</Typography>
							{shootUploadStatus.currentFile ? (
								<Typography variant="caption" color="text.secondary" display="block" mb={1} noWrap>
									Current file: {shootUploadStatus.currentFile}
								</Typography>
							) : null}
							<Typography variant="caption" color="text.secondary" display="block" mb={1}>
								Current channel progress: {shootUploadStatus.channelProgress}%
							</Typography>
							<LinearProgress variant="determinate" value={shootUploadStatus.channelProgress} />
						</Box>
					) : null}
					{shootLevelPreviewData.length ? (
						<Paper elevation={0} sx={{ border: `1px solid ${grey[200]}`, borderRadius: 2, overflow: "hidden" }}>
							<List sx={{ py: 0 }}>
								{shootLevelPreviewData.map((row) => {
									const checked = selectedShootChannels.includes(row.channel);
									return (
										<ListItem
											key={row.channel}
											disablePadding
											secondaryAction={
												<Stack alignItems="flex-end" spacing={0.25} pr={2}>
													<Typography variant="caption" color="text.secondary">
														{row.fileCount} new files
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{row.skuCount} SKUs
													</Typography>
												</Stack>
											}>
											<ListItemButton
												disabled={isShootLevelUploading}
												onClick={() => {
													setSelectedShootChannels((prev) =>
														prev.includes(row.channel)
															? prev.filter((item) => item !== row.channel)
															: [...prev, row.channel],
													);
												}}>
												<ListItemIcon sx={{ minWidth: 40 }}>
													<Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
												</ListItemIcon>
												<ListItemText
													primary={row.channelLabel}
													secondary={`${row.existingCount} images already present in this shoot for ${row.channelLabel}`}
													primaryTypographyProps={{ textTransform: "capitalize", fontWeight: 600 }}
												/>
											</ListItemButton>
										</ListItem>
									);
								})}
							</List>
						</Paper>
					) : (
						<Paper elevation={0} sx={{ border: `1px dashed ${grey[300]}`, borderRadius: 2, p: 2 }}>
							<Typography variant="body2" color="text.secondary">
								Fix the folder issues above and choose the folder again. Valid channels from this selection will
								appear here.
							</Typography>
						</Paper>
					)}
				</Box>
				<DialogActions>
					<Button variant="outlined" onClick={handleShootLevelPreviewClose} disabled={isShootLevelUploading}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleConfirmShootLevelUpload}
						disabled={isShootLevelUploading || !selectedShootChannels.length}>
						{isShootLevelUploading ? "Uploading..." : "Start Upload"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ShootSkuImages;
